import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import Dropzone from "react-dropzone";

import { useGlobalContext } from "app/store/GlobalContext";
import { useInboxReplyContext } from "app/store/InboxReplyContext";
import { Alert } from "components/alert/Alert";
import { Button } from "components/button/Button";
import { UPLOAD_FILES_LIMIT } from "components/fileInput/fileInputConsts";
import { Icon } from "components/icon/Icon";
import { Input } from "components/input/Input";
import { Loader } from "components/loader/Loader";
import Modal from "components/modal/Modal";
import UnsavedChangesModal from "components/shared/unsavedChangesModal/UnsavedChangesModal";
import TextEditor from "components/textEditor/TextEditor";
import { filterDuplicateFiles } from "helpers/FilterDuplicateFiles";
import { useDebouncedContentValidation } from "hooks/use-debounced-content-validation";
import { usePermission } from "hooks/use-permissions";
import { useStickyActionBar } from "hooks/use-sticky-action-bar";
import { Attachment, Subscriber } from "models/dashboard/dashboard";
import { DocumentUploadFile } from "models/DocumentUploadFile";
import { Thread, ThreadCategory } from "models/inbox/inbox";
import { useLocation } from "react-router";
import { useRootStore } from "store";
import { MessageType } from "store/slices/inboxSlice";
import DocumentArchiveModal from "views/documents/containers/DocumentArchiveModal";
import {
  NODE_TYPE_FOLDER,
  NODE_TYPE_ROOT
} from "views/documents/state/documentArchiveOptions";

import ThreadActionBar from "views/inbox/components/Inbox/main/threadPreview/ThreadActionBar";
import RecipientsSelectorWithAbsenceMessage from "views/inbox/components/recipientsSelectorWithAbsenceMessage/RecipientsSelectorWithAbsenceMessage";
import ContentSizeAlert from "../ContentSizeAlert";

import "../CreateThread.scss";

type Props = {
  defaultRecipients: Subscriber[];
  isSending: boolean;
  onSend: (
    title: string,
    body: string,
    recipients: string[],
    attachments: Attachment[],
    filesToUpload: DocumentUploadFile[],
    messageType?: string,
    reportStart?: string,
    reportEnd?: string,
    category?: ThreadCategory
  ) => void;
  onDiscardClicked?: () => void;
  showSubjectLine?: boolean;
  thread?: Thread;
  showBackIcon?: boolean;
  onValidationChange?: (isValid: boolean) => void;
  hideActions?: boolean;
};

export interface CreateThreadMobileHandle {
  sendMessage: () => void;
  openArchiveModal: () => void;
  triggerFileSelect: () => void;
}

const CreateThreadMobile = forwardRef<CreateThreadMobileHandle, Props>(
  (
    {
      defaultRecipients,
      showSubjectLine,
      isSending,
      thread,
      showBackIcon,
      onSend,
      onDiscardClicked,
      onValidationChange,
      hideActions = false
    },
    ref
  ) => {
    const { editingState, setEditingState, redirectingState } =
      useGlobalContext();
    const currentLocation = useLocation();
    const { handleNavigation } = useGlobalContext();
    const { formState, updateFormState, markDirty, clearDirty } =
      useInboxReplyContext();

    const isInternalUser = usePermission("isInternal");
    const { user, archive } = useRootStore((state) => state);
    const { workingCustomer: customer } = useRootStore(
      (state) => state.customers
    );
    const { addNotification } = useRootStore(
      (state) => state.toastNotifications
    );
    const { customerNumber, internalUsers, externalUsers } = customer;
    const {
      getArchiveFolderFiles,
      setSelectedArchiveNodeId,
      toggleArchiveTreeNodeId
    } = archive;

    const [showRecipientModal, setShowRecipientModal] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [hasTooManyFiles, setHasTooManyFiles] = useState(false);

    // Extract form state from context
    const {
      recipients,
      title,
      text,
      attachments,
      computerAttachments,
      selectedCategory,
      validation
    } = formState;

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Use the custom hook to manage sticky action bar positioning
    const actionBarFixed = useStickyActionBar();

    // Debounced content validation hook
    useDebouncedContentValidation({
      text,
      validation,
      recipients,
      showSubjectLine,
      updateFormState,
      onValidationChange
    });

    // Cleanup function on component unmount
    useEffect(() => {
      return () => {
        setEditingState({ isEditing: false, location: null });
      };
    }, [setEditingState]);

    const handleOnRecipientsChange = (threadRecipients: any[]) => {
      setEditingState({ isEditing: true, location: "conversation" });
      updateFormState({ recipients: threadRecipients });
      markDirty();
    };

    const handleOnSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      setEditingState({ isEditing: true, location: "conversation" });
      markDirty();

      const newTitleValidation =
        showSubjectLine || (value.length > 0 && value.length <= 78)
          ? "VALID"
          : "INVALID";
      updateFormState({
        title: value,
        validation: { ...validation, title: newTitleValidation }
      });

      if (onValidationChange) {
        const valid =
          recipients.length > 0 &&
          (!showSubjectLine || newTitleValidation === "VALID") &&
          validation.body === "VALID" &&
          validation.contentSize === "VALID";
        onValidationChange(valid);
      }
    };

    const onTextChange = (value: string) => {
      markDirty();
      setEditingState({ isEditing: true, location: "conversation" });

      // Update text immediately for responsive UI
      updateFormState({ text: value });
    };

    const isValid = () => {
      return (
        recipients.length > 0 &&
        (!showSubjectLine || validation.title === "VALID") &&
        validation.body === "VALID" &&
        validation.contentSize === "VALID"
      );
    };

    const handleSendMessage = () => {
      if (!recipients.length) {
        setShowRecipientModal(!showRecipientModal);
        return;
      }
      if (isValid()) {
        onSend(
          title,
          text,
          recipients.map((recipient) => recipient.id),
          attachments,
          computerAttachments,
          MessageType.Normal,
          undefined,
          undefined,
          selectedCategory
        );
        clearDirty();
      }
    };

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      sendMessage: handleSendMessage,
      openArchiveModal: () => setShowArchiveModal(true),
      triggerFileSelect: () => {
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }
    }));

    const handleTreeNodeClicked = (node: Attachment) => {
      if (node.type === NODE_TYPE_FOLDER || node.type === NODE_TYPE_ROOT) {
        toggleArchiveTreeNodeId(node.id);
        setSelectedArchiveNodeId(node.id);
        getArchiveFolderFiles(customerNumber, node.id);
      } else {
        if (attachments.find((attachment) => attachment.id === node.id)) {
          addNotification(
            "warning",
            `Följande fil är redan uppladdad: ${node.name}`
          );
          return;
        }

        setEditingState({ isEditing: true, location: "conversation" });
        updateFormState({ attachments: [...attachments, node] });
        markDirty();
        setShowArchiveModal(false);
      }
    };

    const handleOnRemove = (item: Attachment) => {
      const updatedAttachments = attachments.filter((attachment) => {
        return attachment.id !== item.id;
      });
      updateFormState({ attachments: updatedAttachments });
      markDirty();
      setShowArchiveModal(false);
    };

    const clearFileInput = () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    const handleOnComputerRemove = (index: number) => {
      const updatedAttachments = computerAttachments.filter(
        (_, i) => i !== index
      );
      updateFormState({ computerAttachments: updatedAttachments });
      markDirty();
      setHasTooManyFiles(false);
      clearFileInput();
    };

    const handleNavigateBack = (
      e: React.MouseEvent<HTMLElement, MouseEvent>
    ) => {
      handleNavigation({
        url: -1,
        eventTitle: "Back",
        e: e
      });
    };

    const addNewFilesAndCheckLimit = (addFiles: File[]) => {
      setEditingState({ isEditing: true, location: "conversation" });

      const newFilesToAdd = filterDuplicateFiles(
        addFiles,
        computerAttachments.map((attachment) => attachment.file)
      );

      const existingFiles = [
        ...computerAttachments,
        ...newFilesToAdd.map((file) => ({
          file: file,
          meta: {
            fileName: file.name,
            alternativeFileName: undefined
          }
        }))
      ];

      setHasTooManyFiles(existingFiles.length > UPLOAD_FILES_LIMIT);
      updateFormState({
        computerAttachments: existingFiles.slice(0, UPLOAD_FILES_LIMIT)
      });
      markDirty();
      clearFileInput();
    };

    const userInfoId = user.userInfo ? user.userInfo.id : "";
    const SSC = "SSC";

    const availableInternalUsers = (internalUsers || []).filter(
      (availableInternalUser) => availableInternalUser.organization !== SSC
    );

    const availableRecipients = [
      ...availableInternalUsers,
      ...externalUsers
    ].filter((availableUser) => availableUser.id !== userInfoId);

    const selectedRecipients = recipients.filter(
      (recipient) => recipient.id !== userInfoId
    );

    return (
      <div className="w-100 h-100">
        <div className="thread-preview-mobile-container">
          <div className="thread-content-scrollable">
            {!user.userInfo && <Loader />}
            {showRecipientModal && (
              <Modal
                header={<h1>Mottagare saknas</h1>}
                onDismiss={() => setShowRecipientModal(false)}
                modalTitle="recipient missing"
              >
                <p>
                  Vänligen lägg till en mottagare för att skicka meddelandet.
                </p>
                <Button
                  onClick={() => setShowRecipientModal(!showRecipientModal)}
                >
                  Ok
                </Button>
              </Modal>
            )}

            {showArchiveModal && (
              <DocumentArchiveModal
                header={<h1 className="mt-0">Bifoga fil</h1>}
                onSelectFile={handleTreeNodeClicked}
                onDismiss={() => setShowArchiveModal(false)}
              />
            )}

            <div className="editor">
              <div className="d-flex align-items-center mb-4">
                {showBackIcon && (
                  <span className="thread-meta-arrow mr-3 mt-1">
                    <Icon
                      name={`fas fa-chevron-left cursor-pointer`}
                      onClick={(e) => handleNavigateBack(e)}
                    />
                  </span>
                )}

                <label>Lägg till mottagare</label>
              </div>

              <RecipientsSelectorWithAbsenceMessage
                selectedRecipients={selectedRecipients}
                availableRecipients={availableRecipients}
                onChange={handleOnRecipientsChange}
              />

              <div className="mt-4 text-editor-input-wrapper">
                <label className="mb-3">Ämne</label>
                <Input
                  maxLength={78}
                  onChange={handleOnSubjectChange}
                  type="text"
                  placeholder="Skriv rubrik"
                  value={title}
                  disabled={!!thread}
                />
              </div>

              <div className="mt-4">
                <label className="mb-3">Meddelande</label>
                <TextEditor
                  value={text}
                  placeholder="Skriv meddelande"
                  onChange={onTextChange}
                  minimalMode={true}
                />
                <ContentSizeAlert
                  isInvalid={validation.contentSize === "INVALID"}
                />
              </div>

              <div className="mt-4">
                {attachments.length + computerAttachments.length > 0 && (
                  <label className="mb-3">Bifogade filer</label>
                )}

                {attachments.map((attachment) => {
                  return (
                    <div
                      key={attachment.id}
                      className="cursor-pointer"
                      onClick={() => handleOnRemove(attachment)}
                    >
                      <Icon
                        name="times"
                        className="text-ludvig-red mr-3 mb-2"
                      />
                      {attachment.name}
                    </div>
                  );
                })}

                {computerAttachments.map((attachment, i) => {
                  return (
                    <div
                      key={attachment.file.name}
                      className="cursor-pointer"
                      onClick={() => handleOnComputerRemove(i)}
                    >
                      <Icon
                        name="times"
                        className="text-ludvig-red mr-3 mb-2"
                      />
                      {attachment.file.name}
                    </div>
                  );
                })}

                {hasTooManyFiles && (
                  <Alert
                    className="mt-2"
                    variant="warning"
                    onDismiss={() => setHasTooManyFiles(false)}
                  >
                    <Icon name="exclamation-circle" className="mr-2" />
                    <span>Du kan ladda upp max femton filer åt gången.</span>
                  </Alert>
                )}
              </div>

              {/* Hidden Dropzone for file upload - triggered by ThreadActionBar */}
              <div style={{ display: "none" }}>
                <Dropzone onDrop={addNewFilesAndCheckLimit}>
                  {({ getRootProps, getInputProps }) => {
                    const inputProps = getInputProps();
                    return (
                      <div {...getRootProps()}>
                        <input {...inputProps} ref={fileInputRef} />
                      </div>
                    );
                  }}
                </Dropzone>
              </div>
            </div>
          </div>

          {/* Action bar positioned outside scrollable content */}
          {!hideActions && (
            <div
              className={`thread-action-bar-container ${actionBarFixed ? "fixed-to-viewport" : "fixed-to-component"}`}
            >
              <ThreadActionBar
                mobile
                isReplying={true}
                onSendReply={handleSendMessage}
                onAttach={() => fileInputRef.current?.click()}
                onAttachFromArchive={
                  isInternalUser ? () => setShowArchiveModal(true) : undefined
                }
                onSaveDraft={() => {
                  // TODO: Implement save draft functionality
                  console.log("Save draft");
                }}
                onDiscardDraft={onDiscardClicked}
                canSend={isValid()}
                isSending={isSending}
                isCreatingNewThread={!thread}
                selectedCategory={selectedCategory}
                onCategoryChange={(category) => {
                  updateFormState({ selectedCategory: category });
                  markDirty();
                }}
              />
            </div>
          )}
          {!showSubjectLine && <hr className="hr-divider"></hr>}
          {editingState.isEditing &&
            editingState.location === "conversation" &&
            redirectingState.isRedirecting &&
            redirectingState.url !== currentLocation.pathname && (
              <UnsavedChangesModal text="Du har ett osparat meddelande. Är du säker på att du vill lämna sidan?" />
            )}
        </div>
      </div>
    );
  }
);

CreateThreadMobile.displayName = "CreateThreadMobile";

export default CreateThreadMobile;
